import React from 'react'
import { DEVICE } from 'theme'
import { useIsMaxDevice } from 'utils/hooks/useMedia'
import { Box } from 'components/Layout'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { SelectInput } from 'components/SelectInput'
import Datalist from 'components/Datalist'
import Card from 'components/Card'
import { useToaster } from 'utils/useToaster'
import {
  FIELD_DIVIDER_GUTTER,
  DAYS_OF_WEEK,
  DEFAULT_DAY_OF_WEEK,
  POSSIBLE_START_TIMES_OF_DAY,
  getFilteredDayTimesFromStartTimeExcluded,
  SECTION_FIELD_WIDTH,
  CommonProps as Props,
  COMMON_BOTTOM_CONTENT_HEIGHT,
  ADD_BUTTON_WIDTH,
  DEFAULT_START_TIME,
  DEFAULT_END_TIME,
} from '../common'
import { DriveThroughFieldName } from '../useDriveThroughForms'
import { SectionTitle } from './SectionTitle'

const EXISTING_ENTRY_MESSAGE = 'Opening hours for selected day already exist'

type SelectElementType = React.ChangeEvent<HTMLSelectElement>

export const OpeningHours: React.VFC<Props> = ({ driveThroughForm }) => {
  const { setToastMessage } = useToaster()
  const isTabletOrMobile = useIsMaxDevice(DEVICE.DESKTOP)
  const [openingHoursDay, setOpeningHoursDay] = React.useState(DEFAULT_DAY_OF_WEEK)
  const [startTime, setStartTime] = React.useState(DEFAULT_START_TIME)
  const [endTime, setEndTime] = React.useState(DEFAULT_END_TIME)

  const { values, setFieldValue } = driveThroughForm

  const handleRemoveOpeningHoursDay = React.useCallback(
    (openingHoursDay: string) => {
      setFieldValue(
        DriveThroughFieldName.OPENING_HOURS,
        values.openingHours.filter(({ day }) => openingHoursDay !== day)
      )
    },
    [setFieldValue, values.openingHours]
  )

  const handleAddOpeningHoursDay = React.useCallback(() => {
    if (values.openingHours.some(({ day }) => day === openingHoursDay)) {
      setToastMessage(EXISTING_ENTRY_MESSAGE, 'danger')
    } else {
      void setFieldValue(DriveThroughFieldName.OPENING_HOURS, [
        ...values.openingHours,
        { day: openingHoursDay, from: startTime, to: endTime },
      ])
    }
  }, [openingHoursDay, startTime, endTime, setFieldValue, setToastMessage, values.openingHours])

  const handleSelectOpeningHoursDay = React.useCallback((event: SelectElementType) => {
    setOpeningHoursDay(event.target.value)
  }, [])

  const handleSelectStartTime = React.useCallback((event: SelectElementType) => {
    const { value } = event.target
    setStartTime(value)
    setEndTime(getFilteredDayTimesFromStartTimeExcluded(value)[0])
  }, [])

  const handleSelectEndTime = React.useCallback((event: SelectElementType) => {
    setEndTime(event.target.value)
  }, [])

  return (
    <Box pb={COMMON_BOTTOM_CONTENT_HEIGHT}>
      <SectionTitle isTabletOrMobile={isTabletOrMobile}>Opening Hours</SectionTitle>
      <Box
        display={!isTabletOrMobile ? 'flex' : undefined}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box
          display="grid"
          gridTemplateColumns={!isTabletOrMobile ? `repeat(3, ${SECTION_FIELD_WIDTH})` : undefined}
          gridGap={FIELD_DIVIDER_GUTTER}
        >
          <SelectInput
            label="Day"
            value={openingHoursDay}
            onChange={handleSelectOpeningHoursDay}
            options={DAYS_OF_WEEK.map((day) => ({
              label: day,
              value: day,
            }))}
          />
          <SelectInput
            label="From"
            value={startTime}
            onChange={handleSelectStartTime}
            options={POSSIBLE_START_TIMES_OF_DAY.map((day) => ({
              label: day,
              value: day,
            }))}
          />
          <SelectInput
            label="To"
            value={endTime}
            onChange={handleSelectEndTime}
            options={getFilteredDayTimesFromStartTimeExcluded(startTime).map((day) => ({
              label: day,
              value: day,
            }))}
          />
        </Box>
        <Button
          onClick={handleAddOpeningHoursDay}
          mb={isTabletOrMobile ? COMMON_BOTTOM_CONTENT_HEIGHT : '0'}
          width={isTabletOrMobile ? '100%' : ADD_BUTTON_WIDTH}
          leftIcon={<Icon.Plus />}
        >
          Add
        </Button>
      </Box>
      {values.openingHours.length > 0 && (
        <Card>
          <Datalist
            data={values.openingHours}
            renderTableHeader={() => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
                <Datalist.HeaderCell label="day" />
                <Datalist.HeaderCell label="from" />
                <Datalist.HeaderCell label="to" />
                <Datalist.HeaderCell label="action" />
              </Datalist.Row>
            )}
            shouldRenderTableHeader
            renderTableRow={({ day, from, to }) => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
                <Datalist.Cell label="day">{day}</Datalist.Cell>
                <Datalist.Cell label="from">{from}</Datalist.Cell>
                <Datalist.Cell label="to">{to}</Datalist.Cell>
                <Datalist.Cell label="action">
                  <Button
                    size="small"
                    appearance="link"
                    colorVariant="danger"
                    onClick={() => handleRemoveOpeningHoursDay(day)}
                  >
                    <Icon.Trash />
                  </Button>
                </Datalist.Cell>
              </Datalist.Row>
            )}
          />
        </Card>
      )}
    </Box>
  )
}
